import { useState } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { ActionBar } from "../components/ActionBar";
import { DiaryDayRow, DiaryDaySheet } from "../components/CycleDiary";
import { CycleSheet } from "../components/CycleSheet";
import { Hero } from "../components/Hero";
import { Notice } from "../components/Notice";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { I } from "../components/icons";
import { cycleOn } from "../domain/cycle";
import { cycleHistory, diaryCount, symptomTallies } from "../domain/cyclePatterns";
import { DIARY_WORDS, cycleHeading, diaryLine, tallyLine } from "../domain/cycleWords";
import { todayStr } from "../domain/date";
import { useGo } from "../lib/useGo";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";

type View = "day" | "symptom";

/**
 * /progress/cycle/diary — every day she logged, by cycle or by symptom (ADR-060). A record to
 * read and correct, not a score: a day opens the same sheet Today does, for that date, so a past
 * day can be filled in or put right, and the patterns re-read on the next render.
 */
export default function ProgressCycleDiary() {
  const S = useKaya((s) => s.S);
  const go = useGo();
  const [view, setView] = useState<View>("day");
  const [sheetDs, setSheetDs] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const today = todayStr();
  const back = { to: "/progress/cycle", label: "Back to Cycle" };

  if (!cycleOn(S))
    return (
      <IonPage>
        <IonContent>
          <Screen>
            <TopBar title={DIARY_WORDS.title} back={back} />
            <div className="pg">
              <Notice tone="info" secondary={{ label: "Open You › Cycle", onClick: () => go("/you/cycle") }}>
                Cycle tracking is off. Turn it on in You › Cycle and this page fills in.
              </Notice>
            </div>
          </Screen>
        </IonContent>
      </IonPage>
    );

  const count = diaryCount(S, today);
  const cycles = cycleHistory(S, today)
    .filter((c) => c.days.length)
    .reverse();
  const tallies = symptomTallies(S, today);

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title={DIARY_WORDS.title} back={back} />
          <div className="pg">
            <Hero
              label={DIARY_WORDS.label}
              figure={count.days ? count.days : null}
              unit={DIARY_WORDS.unit}
              line={count.since ? diaryLine(count.cycles, count.since) : DIARY_WORDS.empty}
            />

            {count.days ? (
              <Segmented<View>
                ariaLabel="Show the diary"
                value={view}
                onChange={setView}
                options={[
                  { value: "day", label: DIARY_WORDS.byDay },
                  { value: "symptom", label: DIARY_WORDS.bySymptom },
                ]}
              />
            ) : null}

            {view === "day"
              ? cycles.map((c) => (
                  <RowGroup key={c.start} heading={cycleHeading(c.start, c.len, c.current)}>
                    {c.days
                      .slice()
                      .reverse()
                      .map((d) => (
                        <DiaryDayRow key={d.ds} c={c} d={d} onOpen={setSheetDs} />
                      ))}
                  </RowGroup>
                ))
              : null}

            {view === "symptom" && tallies.length ? (
              <RowGroup heading={DIARY_WORDS.symptoms}>
                {tallies.map((t) => (
                  <NavRow
                    key={t.row}
                    title={t.label}
                    subtitle={tallyLine(t.days, t.cycles, t.of)}
                    value={t.pattern ? <Tag tone="cycle">pattern</Tag> : undefined}
                    to={`/progress/cycle/diary/${t.row}`}
                  />
                ))}
              </RowGroup>
            ) : null}
          </div>

          <ActionBar
            secondary={{ label: DIARY_WORDS.another, onClick: () => setPicking(true) }}
            primary={{ label: DIARY_WORDS.logToday, icon: I.plus, onClick: () => setSheetDs(today) }}
          />
        </Screen>

        {sheetDs ? <CycleSheet open onClose={() => setSheetDs(null)} ds={sheetDs} /> : null}
        {picking ? (
          <DiaryDaySheet
            onClose={() => setPicking(false)}
            onOpen={(ds) => {
              setPicking(false);
              setSheetDs(ds);
            }}
          />
        ) : null}
      </IonContent>
    </IonPage>
  );
}
